'use client'

import {
  cloneElement,
  isValidElement,
  useCallback,
  useState,
  type MouseEvent,
  type ReactElement,
  type ReactNode,
} from 'react'
import { LockKeyhole } from 'lucide-react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { getUpgradePlanConfig } from '@/constants/upgrade-plan'
import { navigateWithThreadPreservation } from '@/utils/thread-navigation'

import {
  buildGatingDescription,
  buildGatingHeadline,
  gatingSurfaceTitle,
  planNameForCopy,
  PUBLISH_JOB_FEATURE_KEY,
  PUBLISH_JOB_GATE_BENEFITS,
} from './gating-copy'
import type { GatingQuotaState } from './types'

export type GatePeekProps = {
  allowed: boolean
  featureKey?: string
  featureLabel: string
  planName: string
  upgradePath: string
  description?: string
  quota?: GatingQuotaState
  open?: boolean
  onOpenChange?: (open: boolean) => void
  children: ReactElement
}

/** Inner panel shared by GatePeek and controlled mobile publish gate. */
export function GatePeekPanel({
  featureKey,
  featureLabel,
  planName,
  description,
  quota,
  onUpgrade,
}: {
  featureKey?: string
  featureLabel: string
  planName: string
  upgradePath?: string
  description?: string
  quota?: GatingQuotaState
  onUpgrade: () => void
}) {
  const upgrade = getUpgradePlanConfig()
  const headline = buildGatingHeadline({
    featureKey,
    featureLabel,
    planName,
    quota,
    description,
  })
  const body = buildGatingDescription({
    featureKey,
    featureLabel,
    planName,
    quota,
    description,
  })
  const showPublishBenefits = featureKey === PUBLISH_JOB_FEATURE_KEY && !quota?.exhausted
  return (
    <div className="space-y-3">
      <div className="flex items-start gap-2">
        <LockKeyhole className="mt-0.5 h-4 w-4 shrink-0 text-promotion" aria-hidden />
        <div className="space-y-1">
          <p className="text-sm font-medium leading-snug">{headline}</p>
          {showPublishBenefits ? (
            <ul className="mt-2 space-y-1.5">
              {PUBLISH_JOB_GATE_BENEFITS.map((benefit) => (
                <li
                  key={benefit}
                  className="flex items-start gap-1.5 text-xs text-muted-foreground leading-snug"
                >
                  <span
                    className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-promotion/60"
                    aria-hidden
                  />
                  {benefit}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-xs text-muted-foreground leading-snug">{body}</p>
          )}
        </div>
      </div>
      {upgrade ? (
        <Button type="button" size="sm" className="w-full" onClick={onUpgrade}>
          {upgrade.ctaButtonLabel ?? 'Upgrade plan'}
        </Button>
      ) : null}
    </div>
  )
}

/** T1-action — pre-gated popover for buttons; intercepts click when not allowed. */
export function GatePeek({
  allowed,
  featureKey,
  featureLabel,
  planName,
  upgradePath,
  description,
  quota,
  open: openProp,
  onOpenChange,
  children,
}: GatePeekProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false)
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const open = openProp ?? uncontrolledOpen

  const setOpen = useCallback(
    (next: boolean) => {
      onOpenChange?.(next)
      if (openProp === undefined) {
        setUncontrolledOpen(next)
      }
    },
    [onOpenChange, openProp],
  )

  const handleUpgrade = useCallback(() => {
    setOpen(false)
    navigateWithThreadPreservation(navigate, upgradePath, searchParams)
  }, [navigate, searchParams, setOpen, upgradePath])

  if (allowed) {
    return children
  }

  if (!isValidElement(children)) {
    return children as ReactNode
  }

  const interceptClick = (event: MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()
    setOpen(true)
  }

  const gatedChild = cloneElement(children, {
    onClick: interceptClick,
    'aria-haspopup': 'dialog',
    'aria-expanded': open,
  } as Partial<(typeof children)['props']>)

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{gatedChild}</PopoverTrigger>
      <PopoverContent
        className="w-80"
        align="end"
        role="dialog"
        aria-label={`${gatingSurfaceTitle(featureKey, featureLabel)} requires ${planNameForCopy(planName)}`}
      >
        <GatePeekPanel
          featureKey={featureKey}
          featureLabel={featureLabel}
          planName={planName}
          upgradePath={upgradePath}
          description={description}
          quota={quota}
          onUpgrade={handleUpgrade}
        />
      </PopoverContent>
    </Popover>
  )
}
